import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createJournalEntry, getAccounts } from '../api/gl';
import { useAuth } from '../contexts/AuthContext';
import { formatDateToISO } from '../utils/formatters';

const JournalEntryFormPage = () => {
  const [formData, setFormData] = useState({
    entry_date: formatDateToISO(new Date()),
    description: '',
    reference_number: '',
    lines: [
      { account_id: '', debit_amount: '', credit_amount: '', line_description: '' },
      { account_id: '', debit_amount: '', credit_amount: '', line_description: '' }
    ]
  });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await getAccounts({ transactable_only: true, is_active: true });
      setAccounts(response?.data || []);
    } catch (err) {}
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [
        ...formData.lines,
        { account_id: '', debit_amount: '', credit_amount: '', line_description: '' }
      ]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 2) return;
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };

    // Clear the opposite amount when entering one
    if (field === 'debit_amount' && value) {
      newLines[index].credit_amount = '';
    } else if (field === 'credit_amount' && value) {
      newLines[index].debit_amount = '';
    }

    setFormData({ ...formData, lines: newLines });
  };

  const calculateTotals = () => {
    const totalDebits = formData.lines.reduce(
      (sum, line) => sum + (parseFloat(line.debit_amount) || 0),
      0
    );
    const totalCredits = formData.lines.reduce(
      (sum, line) => sum + (parseFloat(line.credit_amount) || 0),
      0
    );
    return { totalDebits, totalCredits, isBalanced: Math.abs(totalDebits - totalCredits) < 0.01 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { totalDebits, totalCredits, isBalanced } = calculateTotals();

    if (!isBalanced) {
      setError(
        `Entry does not balance. Debits: ${totalDebits.toFixed(2)}, Credits: ${totalCredits.toFixed(2)}`
      );
      setLoading(false);
      return;
    }

    if (totalDebits === 0) {
      setError('Entry must have amounts greater than zero');
      setLoading(false);
      return;
    }

    // Validate all lines have accounts
    const invalidLines = formData.lines.filter(
      (line) => !line.account_id || (!line.debit_amount && !line.credit_amount)
    );

    if (invalidLines.length > 0) {
      setError('All lines must have an account and either a debit or credit amount');
      setLoading(false);
      return;
    }

    try {
      const entryData = {
        entry_date: formData.entry_date,
        description: formData.description,
        reference_number: formData.reference_number,
        entry_type: 'manual',
        lines: formData.lines.map((line) => ({
          account_id: parseInt(line.account_id),
          debit_amount: parseFloat(line.debit_amount) || 0,
          credit_amount: parseFloat(line.credit_amount) || 0,
          line_description: line.line_description
        }))
      };

      await createJournalEntry(entryData);
      navigate('/accounting/journal-entries');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create journal entry');
    } finally {
      setLoading(false);
    }
  };

  const { totalDebits, totalCredits, isBalanced } = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Journal Entry</h1>
          <p className="text-sm text-gray-500">Create a new manual journal entry</p>
        </div>
        <button
          onClick={() => navigate('/accounting/journal-entries')}
          className="text-gray-600 hover:text-gray-900"
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Entry Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Entry Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entry Date *</label>
              <input
                type="date"
                value={formData.entry_date}
                onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference Number
              </label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Optional reference"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
                required
                placeholder="Describe the purpose of this entry"
              />
            </div>
          </div>
        </div>

        {/* Entry Lines */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Entry Lines</h2>
            <button
              type="button"
              onClick={addLine}
              className="text-amber-600 hover:text-amber-700 text-sm font-medium"
            >
              + Add Line
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">Account</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">
                    Description
                  </th>
                  <th className="text-right py-2 px-2 text-sm font-medium text-gray-700">Debit</th>
                  <th className="text-right py-2 px-2 text-sm font-medium text-gray-700">Credit</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {formData.lines.map((line, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-2 px-2">
                      <select
                        value={line.account_id}
                        onChange={(e) => updateLine(index, 'account_id', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        required
                      >
                        <option value="">Select Account</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.account_code} - {account.account_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        value={line.line_description}
                        onChange={(e) => updateLine(index, 'line_description', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        placeholder="Line description"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        value={line.debit_amount}
                        onChange={(e) => updateLine(index, 'debit_amount', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        value={line.credit_amount}
                        onChange={(e) => updateLine(index, 'credit_amount', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-right"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="py-2 px-2">
                      {formData.lines.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeLine(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          X
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td colSpan="2" className="py-3 px-2 text-right">
                    Totals:
                  </td>
                  <td className="py-3 px-2 text-right">{formatCurrency(totalDebits)}</td>
                  <td className="py-3 px-2 text-right">{formatCurrency(totalCredits)}</td>
                  <td></td>
                </tr>
                <tr>
                  <td colSpan="5" className="py-2 px-2">
                    <div
                      className={`text-sm text-right ${isBalanced ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {isBalanced
                        ? 'Entry is balanced'
                        : `Difference: ${formatCurrency(Math.abs(totalDebits - totalCredits))}`}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/accounting/journal-entries')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !isBalanced}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JournalEntryFormPage;
